import "./AccountToolsTab.css";
import React, { useContext, useEffect, useRef, useState } from "react";
import AppContext from "../../../../../context/AppContext";
import { useWindowSize } from "@uidotdev/usehooks";
import Searchbar from "../../../../../components/molecules/searchbar/Searchbar";
import Loader from "../../../../../components/organisms/loader/Loader";
import { useAuth } from "../../../../../context/AuthContext";
import FilledButton from "../../../../../components/molecules/buttons/filledButton/FilledButton";
import UnfilledButton from "../../../../../components/molecules/buttons/unfilledButton/UnfilledButton";
import {
  EstimationCreateButton,
  EstimationReviewButton,
  EstimationTableColumns,
  getEstimationColumnContent,
  EstimationsDropdownMenu,
  filterEstimations,
} from "../../../../../components/organisms/resourcesComponents/EstimationsComponents";
import {
  FinancingPlanCreateButton,
  FinancingPlansReviewButton,
  FinancingPlanTableColumns,
  getFinancingPlanColumnContent,
  FinancingPlanDropdownMenu,
  filterFinancingPlans,
} from "../../../../../components/organisms/resourcesComponents/FinancingPlansComponents";
import {
  CreditSimulationCreateButton,
  CreditSimulationTableColumns,
  CreditSimulatorReviewButton,
  getCreditSimulationColumnContent,
  CreditSimulationDropdownMenu,
  filterCreditSimulations,
} from "../../../../../components/organisms/resourcesComponents/CreditSimulationsComponents";
import { useResources } from "../../../../../context/ResourcesContext";
import ResourceTable from "../../../../../components/organisms/resourceTable/ResourceTable";
import { stringToDate } from "../../../../../utils/Utils";
import { useModals } from "../../../../../context/ModalsContext";
import V1AccessForm from "../../../../../components/forms/account/v1AccessForm/V1AccessForm";
import TransferEstimationForm from "./TransferEstimationForm";

//type = estimations / financingPlans / creditSimulations
function AccountToolsTab({ type, resources, refreshResource = () => {}, isLoaderVisible }) {
  const [searchValue, setSearchValue] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [visibleItems, setVisibleItems] = useState([]);
  const [dropdownResource, setDropdownResource] = useState(null);
  const [resetSearchValueTrigger, setResetSearchValueTrigger] = useState(false);
  const [tableColumns, setTableColumns] = useState([]);

  const { accesses, userData, canAccess } = useAuth();
  const { setModalVisible, setModalContent } = useContext(AppContext);
  const { formatTableColumns, downloadResource, showDuplicateResourceModal, archiveResource, getResourceName } =
    useResources();
  const { showTarifsModal } = useModals();
  const itemsComponents = {
    tableColumns: {
      estimations: EstimationTableColumns,
      financingPlans: FinancingPlanTableColumns,
      creditSimulations: CreditSimulationTableColumns,
    },
    filter: {
      estimations: filterEstimations,
      financingPlans: filterFinancingPlans,
      creditSimulations: filterCreditSimulations,
    },
    getColumnContent: {
      estimations: getEstimationColumnContent,
      financingPlans: getFinancingPlanColumnContent,
      creditSimulations: getCreditSimulationColumnContent,
    },
    dropdownMenu: {
      estimations: EstimationsDropdownMenu,
      financingPlans: FinancingPlanDropdownMenu,
      creditSimulations: CreditSimulationDropdownMenu,
    },
    createButton: {
      estimations: EstimationCreateButton,
      financingPlans: FinancingPlanCreateButton,
      creditSimulations: CreditSimulationCreateButton,
    },
    reviewButton: {
      estimations: EstimationReviewButton,
      financingPlans: FinancingPlansReviewButton,
      creditSimulations: CreditSimulatorReviewButton,
    },
  };

  const { width } = useWindowSize();

  useEffect(() => {
    refreshResource();
  }, []);

  useEffect(() => {
    setTableColumns(
      formatTableColumns(itemsComponents.tableColumns[type.description], width).filter(
        column => column.key !== "archived",
      ),
    );
  }, [type, width]);

  useEffect(() => {
    setResetSearchValueTrigger(!resetSearchValueTrigger);
  }, [activeTab, type]);

  useEffect(() => {
    setVisibleItems(getFilteredItems(resources, type.description));
  }, [activeTab, searchValue, resources, type]);

  useEffect(() => {
    setDropdownResource(null);
  }, [visibleItems]);

  function getFilteredItems(items, typeDescription) {
    if (!items || !typeDescription) return [];
    return items[typeDescription].filter(item => item.archived === !!activeTab);
  }

  function setActiveResource(e, item, index, itemsContainerRef) {
    if (!e) return setDropdownResource(null);

    e.stopPropagation();
    e.preventDefault();

    if (item === dropdownResource?.item) return setDropdownResource(null);

    setDropdownResource({
      item: item,
      itemRef: itemsContainerRef.current.children[index],
      top: itemsContainerRef.current.children[index].offsetTop + 80,
    });
  }

  function showV1Modal() {
    setModalContent({
      title: "Accéder à mes anciennes estimations",
      content: <V1AccessForm />,
      className: "medium-modal",
    });
    setModalVisible(true);
  }

  //Quick actions

  async function downloadItemPdf(item) {
    downloadResource(item, type.description, accesses);
  }

  function showDuplicateItemModal(item) {
    showDuplicateResourceModal(item, type.description, () => refreshResource(type.description));
  }

  async function archiveItem(item, shouldArchive) {
    archiveResource(item, shouldArchive, type.description, () => refreshResource(type.description));
  }

  function openPreview(estimation) {
    if (!estimation?.is_downloadable && !accesses?.includes("unrestricted"))
      return showTarifsModal(
        "Télécharger une ressource",
        `Vous devez être abonné pour télécharger votre ${getResourceName("estimations")}.`,
        "/mon-compte/mes-outils",
      );

    window.open("/pdf/" + estimation.uuid);
  }

  function showTransferModal(estimation) {
    setModalContent({
      title: "Transférer une estimation",
      content: <TransferEstimationForm estimation={estimation} />,
    });
    setModalVisible(true);
  }

  // Table functions

  function getOrderedResources(resources, orderBy) {
    let result = itemsComponents.filter[type.description](resources, searchValue);

    if (!orderBy.key) return result;

    result.sort((a, b) => {
      let valA = itemsComponents.getColumnContent[type.description](orderBy.key, a);
      let valB = itemsComponents.getColumnContent[type.description](orderBy.key, b);

      if (orderBy.key === "type") {
        return valA.description.localeCompare(valB.description, "fr");
      }

      if (orderBy.key.includes("date_")) {
        valA = valA === "-" ? new Date() : stringToDate(valA + ":00");
        valB = valB === "-" ? new Date() : stringToDate(valB + ":00");
        return valA - valB;
      }

      if (typeof valA === "number" && typeof valB === "number") {
        return valA - valB;
      }

      return String(valA).localeCompare(String(valB), "fr", { numeric: true });
    });

    if (orderBy.order === "desc") result.reverse();

    return result;
  }

  const V1Button = ({ userData, type }) => {
    if (type.description !== "estimations" || !userData?.v1_data?.id) return <></>;

    if (userData?.v1_data?.transfer_requested)
      return (
        <UnfilledButton
          bgColor='var(--gold)'
          fontSize='1.3rem'
          borderRadius='5px'
          className='v1-button'
          to={`${process.env.REACT_APP_V1_URL}/tableau-de-bord`}
          target='_blank'>
          Accéder à mes anciennes estimations
        </UnfilledButton>
      );

    return (
      <UnfilledButton
        bgColor='var(--gold)'
        fontSize='1.3rem'
        borderRadius='5px'
        className='v1-button'
        onClick={showV1Modal}>
        Je souhaite accéder à mes anciennes estimations
      </UnfilledButton>
    );
  };

  return (
    <>
      <section>
        <div className='head-row-container'>
          {itemsComponents.createButton[type.description]({ width })}
          <V1Button type={type} userData={userData} />
          {/* {itemsComponents.reviewButton[type.description](
            type.description === "estimations"
              ? {
                  text: "Donnez votre avis sur nos estimations",
                  link: "https://docs.google.com/forms/d/e/1FAIpQLSd_JMXir1jfCfMBZf8fnTK2DcWWw-8MlL8vQ4epWd_pJ4xWbA/viewform?usp=pp_url",
                }
              : type.description === "financingPlans"
                ? {
                    text: "Donnez votre avis sur nos plans de financement",
                    link: "https://docs.google.com/forms/d/e/1FAIpQLSf9YmzgyjBxWJnmTLHHSukHcw9dvK1Kscos1jkxdyYaJOrkHA/viewform?usp=pp_url",
                  }
                : type.description === "creditSimulations"
                  ? {
                      text: "Donnez votre avis sur notre simulateur de prêt",
                      link: "https://docs.google.com/forms/d/e/1FAIpQLScUqDnORP1LLZ4TFgRnNNTRzh_eeHgPY2-5kmPfmwXyQXG5mg/viewform?usp=pp_url",
                    }
                  : {},
          )} */}
        </div>
        <div className='items-container'>
          <div className='items-container-header'>
            <p className={activeTab === 0 ? "active" : ""} onClick={() => setActiveTab(0)}>
              {type.name}
            </p>
            <p className={activeTab === 1 ? "active" : ""} onClick={() => setActiveTab(1)}>
              {type.name} archivé{!type.isMasculine && "e"}s
            </p>
          </div>
          <div className='search-input-tools-container'>
            <Searchbar
              resetValueTrigger={resetSearchValueTrigger}
              onChange={value => setSearchValue(value)}
              placeholder='Rechercher'
              bgColor='var(--white)'
              className='items-search-bar'
            />
          </div>
          <ResourceTable
            dropdownMenu={itemsComponents.dropdownMenu[type.description]({
              activeItem: dropdownResource?.item,
              downloadItemPdf,
              showDuplicateItemModal,
              archiveItem,
              openPreview,
              showTransferModal: canAccess("estimation_transfer_access", accesses) === true ? showTransferModal : null,
            })}
            resourceTypeDescription={type.description}
            resources={visibleItems}
            selectedResource={dropdownResource}
            tableColumns={tableColumns}
            resetOrderByTrigger={resetSearchValueTrigger}
            useCheckbox={false}
            getOrderedResourcesFunction={getOrderedResources}
            getColumnContentFunction={itemsComponents.getColumnContent[type.description]}
            setSelectedResourceFunction={setActiveResource}
          />
          <Loader visible={isLoaderVisible} className='tools-loader' preventScrolling={false} />
        </div>
      </section>
    </>
  );
}

export default AccountToolsTab;
